/** The Settings list (`AppSettingList.listCode`) the floor plan's chair types come from. */
export const CHAIR_TYPE_LIST_CODE = 'chair_types';
/**
 * The chair types a new office starts with — seeded by the migration that
 * creates the list and by the database seed, then the office's to edit in
 * Settings → App Settings.
 */
export const DEFAULT_CHAIR_TYPES = [
    { value: 'chiavari', label: 'Chiavari' },
    { value: 'folding', label: 'Folding' },
    { value: 'ghost', label: 'Ghost' },
    { value: 'cross_back', label: 'Cross-back' },
    { value: 'banquet', label: 'Banquet' },
];
/**
 * The Settings list (`AppSettingList.listCode`) of the parts of an evening a
 * plan can be for — the ceremony, cocktail hour, dinner — each with its own
 * layout of the same room.
 */
export const EVENING_PART_LIST_CODE = 'floor_plan_parts';
/** The parts a new office starts with, then the office's to edit in Settings. */
export const DEFAULT_EVENING_PARTS = [
    { value: 'ceremony', label: 'Ceremony' },
    { value: 'cocktail_hour', label: 'Cocktail hour' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'dancing', label: 'Dancing' },
];
/** When a plan's part begins: a time of day, `HH:MM` on the 24-hour clock. */
export const PLAN_START_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
export function isPlanStartTime(value) {
    return typeof value === 'string' && PLAN_START_TIME_PATTERN.test(value);
}
/** `18:30` as `6:30 PM`; anything that is not a start time as null. */
export function formatPlanStartTime(value) {
    if (!isPlanStartTime(value))
        return null;
    const [h, m] = value.split(':').map(Number);
    const suffix = h < 12 ? 'AM' : 'PM';
    return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}
/**
 * An event's plans in the order the evening runs: those with a start time by
 * it, then those with a part but no time in the order Settings lists the
 * parts (a part Settings no longer has after them), then the rest as given.
 * The sort is stable, so plans that tie keep the order they came in.
 */
export function orderPlansByPart(plans, partOrder) {
    const rank = (p) => {
        if (isPlanStartTime(p.startTime))
            return [0, p.startTime];
        if (p.eveningPart) {
            const i = partOrder.indexOf(p.eveningPart);
            return [1, i < 0 ? partOrder.length : i];
        }
        return [2, 0];
    };
    return plans
        .map((plan, index) => ({ plan, index, key: rank(plan) }))
        .sort((a, b) => {
        if (a.key[0] !== b.key[0])
            return a.key[0] - b.key[0];
        if (a.key[1] < b.key[1])
            return -1;
        if (a.key[1] > b.key[1])
            return 1;
        return a.index - b.index;
    })
        .map((e) => e.plan);
}
/**
 * The grid sizes a plan may be drawn and snapped to, in feet: 6 inches for
 * lining up chairs and aisles, 1 foot (the default), 2 feet, 5 feet for
 * blocking out a large hall. A plan keeps its own (`FloorPlan.gridSizeFt`).
 */
export const FLOOR_PLAN_GRID_SIZES = [0.5, 1, 2, 5];
export const DEFAULT_FLOOR_PLAN_GRID_SIZE = 1;
export function isFloorPlanGridSize(value) {
    return FLOOR_PLAN_GRID_SIZES.includes(value);
}
/** A plan's grid size, or the default when what is stored is not one of the sizes. */
export function floorPlanGridSize(value) {
    return isFloorPlanGridSize(value) ? value : DEFAULT_FLOOR_PLAN_GRID_SIZE;
}
/** The most guests a room's banquet or cocktail capacity may be. */
export const MAX_ROOM_CAPACITY = 100_000;
export const ROOM_TYPES = [
    'Hall',
    'Garden',
    'Pool Area',
    'Beach',
    'Conference Room',
    'Shul',
    'Ballroom',
    'Tent',
    'Rooftop',
    'Courtyard',
];
/**
 * MANUAL: an explicit save. AUTO: an autosave (at most one per interval).
 * RESTORE: the state a restore replaced. REPLACED: another editor's work,
 * kept before a save overwrote it.
 */
export const FLOOR_PLAN_VERSION_KINDS = ['MANUAL', 'AUTO', 'RESTORE', 'REPLACED'];
/**
 * Calculate chair positions around a round table.
 * Returns positions in feet relative to table center.
 */
export function calculateRoundTableChairs(tableWidthFt, seatCount, chairSizeFt = 1.5) {
    const radius = tableWidthFt / 2 + chairSizeFt * 0.7;
    const positions = [];
    for (let i = 0; i < seatCount; i++) {
        const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2;
        positions.push({
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle),
            rotation: ((angle + Math.PI / 2) * 180) / Math.PI,
        });
    }
    return positions;
}
export function calculateRectTableChairs(tableWidthFt, tableHeightFt, seatCount, chairSizeFt = 1.5, sides = 'top-bottom') {
    const positions = [];
    const gap = 0.15; // ~2 inches gap between chair and table edge
    const topY = -(chairSizeFt + gap);
    const bottomY = tableHeightFt + gap;
    const leftX = -(chairSizeFt + gap);
    const rightX = tableWidthFt + gap;
    // Determine which sides get chairs
    const hasTop = sides === 'all' || sides === 'top' || sides === 'top-bottom';
    const hasBottom = sides === 'all' || sides === 'bottom' || sides === 'top-bottom';
    const hasLeft = sides === 'all' || sides === 'left' || sides === 'left-right';
    const hasRight = sides === 'all' || sides === 'right' || sides === 'left-right';
    const sideCount = [hasTop, hasBottom, hasLeft, hasRight].filter(Boolean).length;
    if (sideCount === 0)
        return positions;
    const perSide = Math.ceil(seatCount / sideCount);
    // Helper: distribute N chairs evenly along an edge of length edgeFt
    // Returns center positions with equal gaps between chairs and from edges
    const distribute = (n, edgeFt) => {
        if (n <= 0)
            return [];
        if (n === 1)
            return [edgeFt / 2 - chairSizeFt / 2];
        // Total space needed: n chairs + (n-1) gaps between them
        // gap = (edgeFt - n * chairSizeFt) / (n + 1) for equal edge margins
        const gap = (edgeFt - n * chairSizeFt) / (n + 1);
        const effectiveGap = Math.max(gap, 0); // prevent negative
        return Array.from({ length: n }, (_, i) => effectiveGap + i * (chairSizeFt + effectiveGap));
    };
    if (hasTop) {
        const xs = distribute(perSide, tableWidthFt);
        for (let i = 0; i < xs.length && positions.length < seatCount; i++) {
            positions.push({ x: xs[i], y: topY, rotation: 0 });
        }
    }
    if (hasBottom) {
        const xs = distribute(perSide, tableWidthFt);
        for (let i = 0; i < xs.length && positions.length < seatCount; i++) {
            positions.push({ x: xs[i], y: bottomY, rotation: 180 });
        }
    }
    if (hasLeft) {
        const ys = distribute(perSide, tableHeightFt);
        for (let i = 0; i < ys.length && positions.length < seatCount; i++) {
            positions.push({ x: leftX, y: ys[i], rotation: 270 });
        }
    }
    if (hasRight) {
        const ys = distribute(perSide, tableHeightFt);
        for (let i = 0; i < ys.length && positions.length < seatCount; i++) {
            positions.push({ x: rightX, y: ys[i], rotation: 90 });
        }
    }
    return positions;
}
/**
 * Calculate chair row group dimensions and positions.
 */
export function calculateChairRowGroup(rows, seatsPerRow, chairSpacingFt = 2, rowSpacingFt = 3, chairSizeFt = 1.5) {
    const totalWidthFt = seatsPerRow * chairSpacingFt;
    const totalHeightFt = rows * rowSpacingFt;
    const chairs = [];
    for (let r = 0; r < rows; r++) {
        for (let s = 0; s < seatsPerRow; s++) {
            chairs.push({
                x: s * chairSpacingFt + (chairSpacingFt - chairSizeFt) / 2,
                y: r * rowSpacingFt + (rowSpacingFt - chairSizeFt) / 2,
                rotation: 0,
            });
        }
    }
    return { totalWidthFt, totalHeightFt, chairs };
}
/**
 * Calculate total footprint of a table with chairs in feet.
 */
export function calculateTableFootprint(tableWidthFt, tableHeightFt, isRound, seatCount, chairSizeFt = 1.5) {
    if (isRound) {
        const radius = tableWidthFt / 2 + chairSizeFt * 0.7 + chairSizeFt;
        return { widthFt: radius * 2, heightFt: radius * 2 };
    }
    return {
        widthFt: tableWidthFt + (seatCount > 0 ? 0 : 0),
        heightFt: tableHeightFt + (seatCount > 0 ? chairSizeFt * 2 : 0),
    };
}
/**
 * Calculate distance between two points in feet.
 */
export function calculateDistanceFt(x1, y1, x2, y2) {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}
export const TABLE_GROUP_COLORS = {
    bride: '#f9a8d4',
    groom: '#93c5fd',
    vip: '#fcd34d',
    family: '#86efac',
    general: '#d1d5db',
};
// ─── Canvas geometry, shared by every surface that draws a plan ──────────────
/**
 * The scale a stored plan is in.
 *
 * ⚠️ **`FloorPlanObject.x`/`y` are canvas pixels, not feet**, while
 * `widthFt`/`heightFt` are feet — so nothing can place an object without this
 * number. It lived only in the frontend's canvas helpers, which was fine while
 * the browser was the only thing that drew a plan; the PDF exporter draws the
 * same plan and a second copy of the constant would be a plan that renders in
 * one place and is subtly wrong in the other.
 */
export const PIXELS_PER_FOOT = 20;
export function ftToPx(ft) {
    return ft * PIXELS_PER_FOOT;
}
export function pxToFt(px) {
    return px / PIXELS_PER_FOOT;
}
/** Object types that are tables by their type alone (the built-in library). */
export const TABLE_OBJECT_TYPES = new Set([
    'TABLE_ROUND',
    'TABLE_OVAL',
    'TABLE_SQUARE',
    'TABLE_RECT',
    'TABLE_HEAD',
    'TABLE_SERPENTINE',
    'TABLE_HALF_ROUND',
]);
/**
 * Which outline an object is drawn with.
 *
 * ⚠️ **`config.shape` wins over the type**, because a custom object from
 * Settings carries its shape there and a legacy `TABLE` row carries
 * `config.tableShape`. The fall-throughs below are the legacy ladder, in the
 * order the canvas has always resolved them.
 */
export function resolveFloorPlanShape(objectType, config) {
    const declared = config?.shape;
    if (typeof declared === 'string')
        return declared;
    if (objectType === 'TABLE_ROUND' ||
        objectType === 'TABLE_OVAL' ||
        objectType === 'COCKTAIL_TABLE') {
        return 'CIRCLE';
    }
    if (objectType === 'TABLE_SERPENTINE')
        return 'SERPENTINE';
    if (objectType === 'TABLE_HALF_ROUND')
        return 'HALF_ROUND';
    if (TABLE_OBJECT_TYPES.has(objectType))
        return 'RECTANGLE';
    if (objectType === 'TABLE' && config?.tableShape === 'round')
        return 'CIRCLE';
    return 'RECTANGLE';
}
/**
 * A table's fill and stroke from how full it is — empty, partly seated, full.
 *
 * Overridden by `config.useCustomColor`, which is the producer saying they
 * want that table a particular color regardless of who is sitting at it.
 */
export function tableOccupancyColor(seated, capacity) {
    if (seated === 0)
        return { fill: '#f3f4f6', stroke: '#9ca3af', label: 'Empty' };
    if (seated >= capacity)
        return { fill: '#dcfce7', stroke: '#16a34a', label: 'Full' };
    return { fill: '#fef9c3', stroke: '#ca8a04', label: 'Partial' };
}
//# sourceMappingURL=floor-plan.js.map